import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Button, Alert, Spinner, Modal } from 'react-bootstrap';

import coldemailer_api from './coldemailer_api';
import coldemailer_config from './coldemailer_config';
import './coldemailer.css';
import './coldemailer-styles.css';

const c_ContentTypeOptions = [
	{ value: 'text', label: 'Plain text' },
	{ value: 'html', label: 'HTML' }
];

const c_StatusOptions = [
	{ value: 'draft', label: 'Draft' },
	{ value: 'ready', label: 'Ready' },
	{ value: 'paused', label: 'Paused' }
];

const c_Placeholders = ['{{first_name}}', '{{company_name}}', '{{landing_page_url}}'];

function CampaignEmailView() {
	const navigate = useNavigate();
	const { id } = useParams();

	const [campaign_email, set_campaign_email] = useState(null);
	const [sibling_emails, set_sibling_emails] = useState([]);
	const [form_values, set_form_values] = useState(null);
	const [loading, set_loading] = useState(true);
	const [saving, set_saving] = useState(false);
	const [error, set_error] = useState('');
	const [saved_message, set_saved_message] = useState('');
	const [show_delete_modal, set_show_delete_modal] = useState(false);
	const [deleting, set_deleting] = useState(false);
	const [pending_navigation_id, set_pending_navigation_id] = useState('');

	const build_form_values = (loaded_email) => {
		return {
			name: loaded_email.name ? loaded_email.name : '',
			subject: loaded_email.subject ? loaded_email.subject : '',
			message: loaded_email.message ? loaded_email.message : '',
			content_type: loaded_email.content_type ? loaded_email.content_type : 'text',
			touch_number: loaded_email.touch_number ? String(loaded_email.touch_number) : '1',
			status: loaded_email.status ? loaded_email.status : 'draft'
		};
	};

	const load_campaign_email = async () => {
		set_loading(true);
		set_error('');

		try {
			const loaded_email = await coldemailer_api.get_campaign_email(id);

			set_campaign_email(loaded_email);
			set_form_values(build_form_values(loaded_email));

			// The siblings come from the campaign rather than from navigation state, so paging
			// still works on a refresh or when the URL is opened directly
			if(loaded_email.email_campaign_id) {
				const emails = await coldemailer_api.get_campaign_emails(loaded_email.email_campaign_id);
				set_sibling_emails(emails);
			}
			else {
				set_sibling_emails([]);
			}
		} catch (error) {
			console.log('CampaignEmailView.load_campaign_email - ERROR: ', error);
			set_error('Failed to load the campaign email.');
		} finally {
			set_loading(false);
		}
	};

	// Where this email sits in the campaign's ordered list, or -1 while the list is loading
	const get_current_index = () => {
		for(var i=0; i < sibling_emails.length; ++i) {
			if(sibling_emails[i].id === id) {
				return i;
			}
		}

		return -1;
	};

	const get_previous_email = () => {
		const index = get_current_index();
		if(index < 1) {
			return null;
		}

		return sibling_emails[index - 1];
	};

	const get_next_email = () => {
		const index = get_current_index();
		if((index === -1) || (index >= sibling_emails.length - 1)) {
			return null;
		}

		return sibling_emails[index + 1];
	};

	useEffect(function() {
		load_campaign_email();
	}, [id]);

	const on_field_change = (field_name) => {
		return (event) => {
			const value = event.target.value;
			set_saved_message('');
			set_form_values(function(previous) {
				return Object.assign({}, previous, { [field_name]: value });
			});
		};
	};

	const has_changes = () => {
		if(!campaign_email || !form_values) {
			return false;
		}

		const original = build_form_values(campaign_email);
		const names = Object.keys(original);

		for(var i=0; i < names.length; ++i) {
			if(original[names[i]] !== form_values[names[i]]) {
				return true;
			}
		}

		return false;
	};

	const can_save = () => {
		if(!form_values) {
			return false;
		}

		if(!form_values.subject.trim()) {
			return false;
		}

		if(!form_values.message.trim()) {
			return false;
		}

		return has_changes();
	};

	// Warns rather than blocks, since a placeholder is optional but a typo in one is silent
	const get_unknown_placeholders = () => {
		if(!form_values) {
			return [];
		}

		const found = form_values.message.match(/\{\{[^}]*\}\}/g);
		if(!found) {
			return [];
		}

		const unknown = [];

		for(var i=0; i < found.length; ++i) {
			if(c_Placeholders.indexOf(found[i]) === -1 && unknown.indexOf(found[i]) === -1) {
				unknown.push(found[i]);
			}
		}

		return unknown;
	};

	const handle_save = async () => {
		set_saving(true);
		set_error('');
		set_saved_message('');

		try {
			const updated = await coldemailer_api.update_campaign_email(id, {
				name: form_values.name.trim(),
				subject: form_values.subject.trim(),
				message: form_values.message,
				content_type: form_values.content_type,
				touch_number: parseInt(form_values.touch_number, 10),
				status: form_values.status
			});

			set_campaign_email(updated);
			set_form_values(build_form_values(updated));
			set_saved_message('Email saved.');

			return true;
		} catch (error) {
			console.log('CampaignEmailView.handle_save - ERROR: ', error);

			const message = error.response && error.response.data && error.response.data.message ? error.response.data.message : 'Failed to save the email.';
			set_error(message);

			return false;
		} finally {
			set_saving(false);
		}
	};

	const handle_delete = async () => {
		set_deleting(true);
		set_error('');

		try {
			await coldemailer_api.delete_campaign_email(id);
			handle_back();
		} catch (error) {
			console.log('CampaignEmailView.handle_delete - ERROR: ', error);
			set_error('Failed to delete the email.');
			set_deleting(false);
			set_show_delete_modal(false);
		}
	};

	// Returns to the campaign that owns this email, falling back to the campaign list
	// Paging away with unsaved edits would lose them silently, so the move is held until the
	// user says what to do with them
	const navigate_to_email = (target_email) => {
		if(!target_email) {
			return;
		}

		if(has_changes()) {
			set_pending_navigation_id(target_email.id);
			return;
		}

		navigate(coldemailer_config.campaign_email_route + '/' + target_email.id);
	};

	const handle_previous = () => {
		navigate_to_email(get_previous_email());
	};

	const handle_next = () => {
		navigate_to_email(get_next_email());
	};

	const handle_discard_and_navigate = () => {
		const target_id = pending_navigation_id;
		set_pending_navigation_id('');
		navigate(coldemailer_config.campaign_email_route + '/' + target_id);
	};

	// Stays put when the save fails, so the edits are not lost along with the error
	const handle_save_and_navigate = async () => {
		const target_id = pending_navigation_id;
		set_pending_navigation_id('');

		const saved = await handle_save();
		if(!saved) {
			return;
		}

		navigate(coldemailer_config.campaign_email_route + '/' + target_id);
	};

	const handle_back = () => {
		if(campaign_email && campaign_email.email_campaign_id) {
			navigate(coldemailer_config.campaign_route + '/' + campaign_email.email_campaign_id);
			return;
		}

		navigate(coldemailer_config.campaign_route);
	};

	// Says where this email sits in the campaign, so paging has somewhere to page within
	const render_position_label = () => {
		const index = get_current_index();
		if(index === -1) {
			return '';
		}

		return 'Email ' + (index + 1) + ' of ' + sibling_emails.length;
	};

	const render_header = () => {
		return (
			<div className="ce-header">
				<div>
					<h4 className="ce-title">{campaign_email && campaign_email.name ? campaign_email.name : 'Campaign Email'}</h4>
					<p className="ce-subtitle">Edit this email's copy and settings</p>
				</div>

				<div className="ce-header-actions">
					<Button variant="outline-light" size="sm" className="ce-button" onClick={handle_back}>
						Back
					</Button>

					<Button
						variant="outline-danger"
						size="sm"
						onClick={function() { set_show_delete_modal(true); }}
						disabled={saving}
					>
						Delete
					</Button>

					<Button
						size="sm"
						className="ce-button-create"
						onClick={handle_save}
						disabled={!can_save() || saving}
					>
						{saving ? 'Saving...' : 'Save'}
					</Button>
				</div>
			</div>
		);
	};

	const render_loading = () => {
		return (
			<div className="ce-loading">
				<Spinner animation="border" size="sm" />
			</div>
		);
	};

	const render_placeholder_warning = () => {
		const unknown = get_unknown_placeholders();
		if(!unknown.length) {
			return null;
		}

		return (
			<Alert variant="danger" className="ce-modal-alert">
				Unrecognised placeholder: {unknown.join(', ')}. Only {c_Placeholders.join(', ')} are filled per recipient. Anything else is sent literally.
			</Alert>
		);
	};

	const render_form = () => {
		return (
			<div className="ce-panel">
				<Form>
					<Form.Group className="ce-modal-field">
						<Form.Label className="ce-modal-label">Name</Form.Label>
						<Form.Control
							type="text"
							value={form_values.name}
							onChange={on_field_change('name')}
							disabled={saving}
						/>
						<Form.Text className="ce-modal-help">
							Internal label only. The recipient never sees this.
						</Form.Text>
					</Form.Group>

					<Form.Group className="ce-modal-field">
						<Form.Label className="ce-modal-label">Subject</Form.Label>
						<Form.Control
							type="text"
							value={form_values.subject}
							onChange={on_field_change('subject')}
							disabled={saving}
						/>
					</Form.Group>

					<Form.Group className="ce-modal-field">
						<Form.Label className="ce-modal-label">Message</Form.Label>
						<Form.Control
							as="textarea"
							rows={18}
							className="ce-message-input"
							value={form_values.message}
							onChange={on_field_change('message')}
							disabled={saving}
						/>
						<Form.Text className="ce-modal-help">
							Placeholders filled per recipient: {c_Placeholders.join(', ')}
						</Form.Text>
					</Form.Group>

					<div className="ce-form-row">
						<Form.Group className="ce-modal-field ce-field-touch">
							<Form.Label className="ce-modal-label">Touch Number</Form.Label>
							<Form.Control
								type="number"
								min={1}
								step={1}
								value={form_values.touch_number}
								onChange={on_field_change('touch_number')}
								disabled={saving}
							/>
						</Form.Group>

						<Form.Group className="ce-modal-field ce-field-type">
							<Form.Label className="ce-modal-label">Content Type</Form.Label>
							<Form.Select
								value={form_values.content_type}
								onChange={on_field_change('content_type')}
								disabled={saving}
							>
								{c_ContentTypeOptions.map(function(option) {
									return (
										<option key={option.value} value={option.value}>
											{option.label}
										</option>
									);
								})}
							</Form.Select>
						</Form.Group>

						<Form.Group className="ce-modal-field ce-field-status">
							<Form.Label className="ce-modal-label">Status</Form.Label>
							<Form.Select
								value={form_values.status}
								onChange={on_field_change('status')}
								disabled={saving}
							>
								{c_StatusOptions.map(function(option) {
									return (
										<option key={option.value} value={option.value}>
											{option.label}
										</option>
									);
								})}
							</Form.Select>
						</Form.Group>
					</div>
				</Form>
			</div>
		);
	};

	// Paging sits below the form, since it is a move between records rather than an action
	// on the one being edited
	const render_pager = () => {
		if(sibling_emails.length < 2) {
			return null;
		}

		return (
			<div className="ce-pager">
				<Button
					className="ce-button-create"
					onClick={handle_previous}
					disabled={saving || !get_previous_email()}
				>
					Previous
				</Button>

				<span className="ce-pager-label">{render_position_label()}</span>

				<Button
					className="ce-button-create"
					onClick={handle_next}
					disabled={saving || !get_next_email()}
				>
					Next
				</Button>
			</div>
		);
	};

	const render_unsaved_modal = () => {
		return (
			<Modal show={pending_navigation_id ? true : false} onHide={function() { set_pending_navigation_id(''); }} centered>
				<Modal.Header closeButton>
					<Modal.Title>Unsaved Changes</Modal.Title>
				</Modal.Header>

				<Modal.Body>
					<p>This email has changes that have not been saved.</p>
				</Modal.Body>

				<Modal.Footer>
					<Button variant="secondary" onClick={function() { set_pending_navigation_id(''); }} disabled={saving}>
						Stay Here
					</Button>
					<Button variant="outline-danger" onClick={handle_discard_and_navigate} disabled={saving}>
						Discard Changes
					</Button>
					<Button className="ce-button-create" onClick={handle_save_and_navigate} disabled={!can_save() || saving}>
						{saving ? 'Saving...' : 'Save and Continue'}
					</Button>
				</Modal.Footer>
			</Modal>
		);
	};

	const render_delete_modal = () => {
		return (
			<Modal show={show_delete_modal} onHide={function() { if(!deleting) { set_show_delete_modal(false); } }} centered size="sm">
				<Modal.Header closeButton>
					<Modal.Title>Delete Email</Modal.Title>
				</Modal.Header>

				<Modal.Body>
					<p>Delete <strong>{campaign_email ? campaign_email.name : ''}</strong>?</p>
					<p>This removes it from the campaign permanently.</p>
				</Modal.Body>

				<Modal.Footer>
					<Button variant="secondary" onClick={function() { set_show_delete_modal(false); }} disabled={deleting}>
						Cancel
					</Button>
					<Button variant="danger" onClick={handle_delete} disabled={deleting}>
						{deleting ? 'Deleting...' : 'Delete'}
					</Button>
				</Modal.Footer>
			</Modal>
		);
	};

	const render_content = () => {
		if(loading) {
			return render_loading();
		}

		if(!campaign_email || !form_values) {
			return null;
		}

		return (
			<React.Fragment>
				{render_placeholder_warning()}
				{render_form()}
				{render_pager()}
				{render_unsaved_modal()}
				{render_delete_modal()}
			</React.Fragment>
		);
	};

	const root_style = { '--ce-top-offset': coldemailer_config.top_offset_px + 'px' };

	return (
		<div className="ce-root" style={root_style}>
			{render_header()}

			{error ? <Alert variant="danger">{error}</Alert> : null}
			{saved_message ? <Alert variant="success">{saved_message}</Alert> : null}

			{render_content()}
		</div>
	);
}

export default CampaignEmailView;
